import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowsClockwise,
  Buildings,
  CheckCircle,
  Database,
  FileText,
  FunnelSimple,
  GoogleLogo,
  IdentificationCard,
  ListMagnifyingGlass,
  LockKey,
  MagnifyingGlass,
  PencilSimple,
  PlugsConnected,
  ShieldCheck,
  ShieldStar,
  X,
  UserCircleGear,
  UserMinus,
  UserPlus,
  UsersThree,
  WarningCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DEFAULT_DEPARTMENTS } from "@/hooks/useDepartments";

import { API } from "../lib/api";

const roleLabels = {
  colaborador: "Colaborador",
  gerente: "Gerente",
  autor_area: "Autor da área",
  administrador_total: "Administrador total",
  admin: "Administrador",
  area_manager: "Autor da área",
  operational: "Colaborador",
};

const statusLabels = {
  active: "Ativo",
  inactive: "Inativo",
  blocked: "Bloqueado",
  away: "Afastado",
};

const emptyUserForm = {
  name: "",
  email: "",
  cpf: "",
  registration: "",
  role: "colaborador",
  department: "",
  job_title: "",
  phone: "",
  birth_date: "",
  status: "active",
};

const demoUsers = [
  {
    user_id: "demo-001",
    name: "Ana Martins",
    email: "ana.martins@empresa-teste.com.br",
    cpf: "12345678901",
    role: "administrador_total",
    department: "Tecnologia",
    job_title: "Administradora",
    status: "active",
    first_access_required: false,
    must_change_password: false,
    updated_at: "2026-07-10T08:40:00",
  },
  {
    user_id: "demo-002",
    name: "Carlos Souza",
    email: "carlos.souza@empresa-teste.com.br",
    cpf: "23456789012",
    role: "autor_area",
    department: "Recursos Humanos",
    job_title: "Analista de TI",
    status: "active",
    first_access_required: false,
    must_change_password: false,
    updated_at: "2026-07-10T07:20:00",
  },
  {
    user_id: "demo-003",
    name: "Marina Costa",
    email: "marina.costa@empresa-teste.com.br",
    cpf: "34567890123",
    role: "gerente",
    department: "Tecnologia",
    job_title: "Gerente",
    status: "active",
    first_access_required: true,
    must_change_password: false,
    updated_at: "2026-07-09T18:10:00",
  },
  {
    user_id: "demo-004",
    name: "Paulo Lima",
    email: "paulo.lima@empresa-teste.com.br",
    cpf: "45678901234",
    role: "colaborador",
    department: "Tecnologia",
    job_title: "Assistente",
    status: "active",
    first_access_required: false,
    must_change_password: false,
    updated_at: "2026-07-09T11:30:00",
  },
  {
    user_id: "demo-005",
    name: "Juliana Alves",
    email: "juliana.alves@empresa-teste.com.br",
    cpf: "56789012345",
    role: "autor_area",
    department: "Recursos Humanos",
    job_title: "Analista de RH",
    status: "active",
    first_access_required: false,
    must_change_password: false,
    updated_at: "2026-07-08T02:00:00",
  },
  {
    user_id: "demo-006",
    name: "Rafael Mendes",
    email: "rafael.mendes@empresa-teste.com.br",
    cpf: "67890123456",
    role: "colaborador",
    department: "Recursos Humanos",
    job_title: "Assistente de RH",
    status: "active",
    first_access_required: false,
    must_change_password: false,
    updated_at: "2026-07-08T02:00:00",
  },
  {
    user_id: "demo-007",
    name: "Bianca Rocha",
    email: "bianca.rocha@empresa-teste.com.br",
    cpf: "78901234567",
    role: "colaborador",
    department: "Recursos Humanos",
    job_title: "Auxiliar",
    status: "inactive",
    first_access_required: false,
    must_change_password: false,
    updated_at: "2026-07-08T02:00:00",
  },
];

function onlyDigits(value = "") {
  return String(value).replace(/\D/g, "");
}

function maskCpf(value = "") {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length !== 11) return digits || "Não informado";
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function formatCpfInput(value = "") {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

function normalizeUserPayload(form) {
  return {
    ...form,
    email: form.email.trim().toLowerCase(),
    cpf: onlyDigits(form.cpf),
    phone: form.phone.trim(),
    registration: form.registration.trim(),
    department: form.department.trim(),
    job_title: form.job_title.trim(),
  };
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatDate(value) {
  if (!value) return "Sem registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sem registro";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function Initials({ name }) {
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl bg-[#eef1ff] text-xs font-bold text-[#2346ff]">
      {getInitials(name)}
    </span>
  );
}

function StatusBadge({ status }) {
  const normalized = status || "active";
  const palette = {
    active: "bg-emerald-100 text-emerald-700",
    inactive: "bg-rose-100 text-rose-700",
    blocked: "bg-amber-100 text-amber-700",
    away: "bg-slate-100 text-slate-600",
  };

  return (
    <Badge className={palette[normalized] || palette.away}>
      {statusLabels[normalized] || normalized}
    </Badge>
  );
}

function AccessBadge({ user }) {
  if (user.must_change_password) {
    return <Badge className="bg-amber-100 text-amber-700">Troca obrigatória</Badge>;
  }
  if (user.first_access_required) {
    return <Badge className="bg-[#eef1ff] text-[#2346ff]">Primeiro acesso</Badge>;
  }
  return <Badge className="bg-emerald-100 text-emerald-700">Senha ativa</Badge>;
}

export default function AdminTotalPage() {
  const { demoMode } = useAuth();
  const [users, setUsers] = useState(demoUsers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lastSync, setLastSync] = useState("Hoje, 02:00");
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [savingUser, setSavingUser] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [sheetsValidation, setSheetsValidation] = useState(null);
  const [dataSource, setDataSource] = useState(demoMode ? "demo" : "loading");
  const [departmentRecords, setDepartmentRecords] = useState(
    DEFAULT_DEPARTMENTS.map(name => ({ department_id: `default-${name}`, name, is_active: true }))
  );
  const [newDepartment, setNewDepartment] = useState("");
  const [departmentSaving, setDepartmentSaving] = useState(false);

  const loadDepartmentRecords = useCallback(async () => {
    if (demoMode) {
      setDepartmentRecords(DEFAULT_DEPARTMENTS.map(name => ({ department_id: `demo-${name}`, name, is_active: true })));
      return;
    }

    try {
      const { data } = await axios.get(`${API}/departments?include_inactive=true`, { withCredentials: true });
      setDepartmentRecords(Array.isArray(data) ? data : []);
    } catch {
      setDepartmentRecords(DEFAULT_DEPARTMENTS.map(name => ({ department_id: `fallback-${name}`, name, is_active: true })));
    }
  }, [demoMode]);

  useEffect(() => {
    const buildDemoValidation = () => ({
      source: "demo",
      is_valid: true,
      total_valid_users: demoUsers.length,
      total_errors: 0,
      role_counts: demoUsers.reduce((acc, user) => {
        acc[user.role] = (acc[user.role] || 0) + 1;
        return acc;
      }, {}),
      status_counts: demoUsers.reduce((acc, user) => {
        const status = user.status || "active";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
      department_counts: demoUsers.reduce((acc, user) => {
        const department = user.department || "Sem setor";
        acc[department] = (acc[department] || 0) + 1;
        return acc;
      }, {}),
      errors: [],
    });

    if (demoMode) {
      setUsers(demoUsers);
      setSheetsValidation(buildDemoValidation());
      setDataSource("demo");
      loadDepartmentRecords();
      return;
    }

    const loadUsers = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/admin/sheets-users/preview?include_cpf=true`, { withCredentials: true });
        setUsers(Array.isArray(data.users) ? data.users : []);
        setSheetsValidation({
          source: data.source,
          is_valid: (data.errors || []).length === 0,
          total_valid_users: data.total_valid_users,
          total_errors: data.total_errors,
          errors: data.errors || [],
        });
        setDataSource("sheets");
        setLastSync("Agora");
      } catch (err) {
        setError(err.response?.data?.detail || "Não foi possível carregar a base de usuários.");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
    loadDepartmentRecords();
  }, [demoMode, loadDepartmentRecords]);

  const filteredUsers = useMemo(() => {
    const term = query.trim().toLowerCase();
    return users.filter(user => {
      const searchable = [
        user.name,
        user.email,
        user.cpf,
        user.department,
        user.job_title,
        roleLabels[user.role],
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesTerm = !term || searchable.includes(term);
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      const matchesStatus = statusFilter === "all" || (user.status || "active") === statusFilter;
      return matchesTerm && matchesRole && matchesStatus;
    });
  }, [users, query, roleFilter, statusFilter]);

  const stats = useMemo(() => {
    const active = users.filter(user => (user.status || "active") === "active").length;
    const authors = users.filter(user => ["autor_area", "area_manager"].includes(user.role)).length;
    const managers = users.filter(user => user.role === "gerente").length;
    const firstAccess = users.filter(user => user.first_access_required || user.must_change_password).length;

    return [
      { label: "Usuários ativos", value: active, icon: UsersThree },
      { label: "Autores de área", value: authors, icon: UserPlus },
      { label: "Gerentes", value: managers, icon: UserCircleGear },
      { label: "Acessos pendentes", value: firstAccess, icon: LockKey },
    ];
  }, [users]);

  const departments = useMemo(() => {
    const map = users.reduce((acc, user) => {
      const department = user.department || "Sem setor";
      acc[department] = (acc[department] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [users]);

  const activeDepartments = useMemo(
    () => departmentRecords.filter(department => department.is_active !== false),
    [departmentRecords]
  );

  const createDepartment = async () => {
    const name = newDepartment.trim();
    if (!name) return;

    setDepartmentSaving(true);
    setError("");
    try {
      if (demoMode) {
        setDepartmentRecords(current => [
          ...current,
          { department_id: `demo-${Date.now()}`, name, description: "", is_active: true },
        ]);
      } else {
        await axios.post(`${API}/departments`, { name }, { withCredentials: true });
        await loadDepartmentRecords();
      }
      setNewDepartment("");
    } catch (err) {
      setError(err.response?.data?.detail || "Nao foi possivel criar o departamento.");
    } finally {
      setDepartmentSaving(false);
    }
  };

  const deactivateDepartment = async (department) => {
    if (!window.confirm(`Desativar o departamento ${department.name}?`)) return;
    setError("");
    try {
      if (demoMode) {
        setDepartmentRecords(current => current.map(item => (
          item.department_id === department.department_id ? { ...item, is_active: false } : item
        )));
      } else {
        await axios.delete(`${API}/departments/${department.department_id}`, { withCredentials: true });
        await loadDepartmentRecords();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Nao foi possivel desativar o departamento.");
    }
  };

  const handleManualSync = async () => {
    setLoading(true);
    setError("");
    setSyncSummary(null);

    if (demoMode) {
      window.setTimeout(() => {
        setSyncSummary({ total_valid_users: users.length, total_errors: 0, is_valid: true });
        setLastSync("Agora");
        setLoading(false);
      }, 700);
      return;
    }

    try {
      const validationResponse = await axios.get(`${API}/admin/sheets-users/validate`, { withCredentials: true });
      const previewResponse = await axios.get(`${API}/admin/sheets-users/preview?include_cpf=true`, { withCredentials: true });
      setSheetsValidation(validationResponse.data);
      setSyncSummary(validationResponse.data);
      setUsers(Array.isArray(previewResponse.data.users) ? previewResponse.data.users : []);
      setDataSource("sheets");
      setLastSync("Agora");
    } catch (err) {
      setError(err.response?.data?.detail || "Não foi possível sincronizar a planilha.");
    } finally {
      setLoading(false);
    }
  };

  const openNewUserForm = () => {
    setEditingUser(null);
    setUserForm(emptyUserForm);
    setError("");
    setFormOpen(true);
  };

  const openEditUserForm = (selectedUser) => {
    setEditingUser(selectedUser);
    setUserForm({
      ...emptyUserForm,
      name: selectedUser.name || "",
      email: selectedUser.email || "",
      cpf: formatCpfInput(selectedUser.cpf || ""),
      registration: selectedUser.registration || "",
      role: selectedUser.role || "colaborador",
      department: selectedUser.department || "",
      job_title: selectedUser.job_title || "",
      phone: selectedUser.phone || "",
      birth_date: selectedUser.birth_date || "",
      status: selectedUser.status || "active",
    });
    setError("");
    setFormOpen(true);
  };

  const closeUserForm = () => {
    setFormOpen(false);
    setEditingUser(null);
    setUserForm(emptyUserForm);
  };

  const updateUserForm = (field, value) => {
    setUserForm(current => ({
      ...current,
      [field]: field === "cpf" ? formatCpfInput(value) : value,
    }));
  };

  const handleUserSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const payload = normalizeUserPayload(userForm);
    if (!payload.name || !payload.email || !payload.cpf || !payload.birth_date) {
      setError("Preencha nome, e-mail, CPF e data de nascimento para liberar o primeiro acesso.");
      return;
    }

    if (payload.cpf.length !== 11) {
      setError("CPF precisa ter 11 dígitos.");
      return;
    }

    setSavingUser(true);
    try {
      if (demoMode) {
        if (editingUser) {
          setUsers(current => current.map(user => (
            (user.user_id || user.email) === (editingUser.user_id || editingUser.email)
              ? { ...user, ...payload, updated_at: new Date().toISOString() }
              : user
          )));
        } else {
          setUsers(current => [
            {
              ...payload,
              user_id: `demo-${Date.now()}`,
              first_access_required: true,
              must_change_password: false,
              updated_at: new Date().toISOString(),
            },
            ...current,
          ]);
        }
        closeUserForm();
        return;
      }

      if (editingUser) {
        const { data } = await axios.put(
          `${API}/users/${editingUser.user_id}`,
          payload,
          { withCredentials: true }
        );
        setUsers(current => current.map(user => (
          user.user_id === editingUser.user_id ? data : user
        )));
      } else {
        const { data } = await axios.post(`${API}/users`, payload, { withCredentials: true });
        setUsers(current => [data, ...current]);
      }
      closeUserForm();
    } catch (err) {
      setError(err.response?.data?.detail || "Não foi possível salvar o usuário.");
    } finally {
      setSavingUser(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#eef1ff] px-3 py-1.5 text-xs font-semibold text-[#2346ff]">
            <ShieldStar className="h-4 w-4" weight="regular" />
            Ambiente restrito ao Administrador Total
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-950">Administração Total</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Gerencie a base de colaboradores, CPF, hierarquia, permissões, setores, auditoria e sincronização com a planilha oficial.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="h-11 rounded-xl">
            <Database className="mr-2 h-4 w-4 text-[#2346ff]" weight="regular" />
            Exportar base
          </Button>
          <Button onClick={openNewUserForm} className="h-11 rounded-xl bg-[#2346ff] text-white hover:bg-[#1d3ace]">
            <UserPlus className="mr-2 h-4 w-4" weight="regular" />
            Novo usuário
          </Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map(({ label, value, icon: Icon }) => (
          <div key={label} className="bento-card flex items-center gap-3 p-4">
            <div className="soft-icon h-11 w-11">
              <Icon className="h-5 w-5" weight="regular" />
            </div>
            <div>
              <p className="font-heading text-2xl font-bold text-slate-950">{value}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <section className="bento-card cute-surface p-5 lg:col-span-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-base font-bold text-slate-900">Sincronização da planilha</h2>
              <p className="text-xs text-slate-500">Google Sheets como fonte inicial da base.</p>
            </div>
            <ArrowsClockwise className="h-5 w-5 text-[#2346ff]" weight="regular" />
          </div>

          <div className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
              <GoogleLogo className="h-6 w-6" weight="regular" />
            </span>
            <div>
              <p className="text-sm font-bold text-slate-800">Base de colaboradores</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Última sincronização: {lastSync}. Rotina manual + automática prevista para o VPS.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            {[
              ["CPF", "Chave única"],
              ["Perfil", "Permissões"],
              ["Status", "Ativo/inativo"],
              ["Nascimento", "1º acesso"],
            ].map(([value, label]) => (
              <div key={value} className="rounded-2xl border border-slate-200 bg-white p-3">
                <p className="font-heading text-sm font-bold text-[#2346ff]">{value}</p>
                <p className="text-[11px] font-medium text-slate-500">{label}</p>
              </div>
            ))}
          </div>

          <Button
            onClick={handleManualSync}
            disabled={loading}
            className="mt-4 h-11 w-full rounded-xl bg-[#2346ff] text-white hover:bg-[#1d3ace]"
          >
            <ArrowsClockwise className="mr-2 h-4 w-4" weight="regular" />
            {loading ? "Validando..." : "Validar planilha agora"}
          </Button>

          {(syncSummary || sheetsValidation) && (
            <div className="mt-4 rounded-2xl border border-[#2346ff]/15 bg-[#f6f8ff] p-3">
              <p className="mb-2 text-xs font-bold text-slate-700">Resumo da última sincronização</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                <span>Fonte: <strong>{dataSource === "sheets" ? "Google Sheets" : dataSource === "backend" ? "Backend" : "Demo"}</strong></span>
                <span>Validos: <strong>{(syncSummary || sheetsValidation)?.total_valid_users ?? users.length}</strong></span>
                <span>Erros: <strong>{(syncSummary || sheetsValidation)?.total_errors ?? 0}</strong></span>
                <span>Status: <strong>{(syncSummary || sheetsValidation)?.is_valid === false ? "Revisar" : "OK"}</strong></span>
              </div>
              {(syncSummary || sheetsValidation)?.errors?.length > 0 && (
                <p className="mt-2 text-xs leading-5 text-amber-700">
                  {(syncSummary || sheetsValidation).errors.length} linha(s) precisam de revisão na planilha.
                </p>
              )}
            </div>
          )}
        </section>

        {formOpen && (
          <section className="bento-card p-5 lg:col-span-4">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-base font-bold text-slate-900">
                  {editingUser ? "Editar usuário" : "Novo usuário"}
                </h2>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Dados mínimos para primeiro acesso: nome, e-mail, CPF e data de nascimento.
                </p>
              </div>
              <button
                type="button"
                onClick={closeUserForm}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-[#2346ff]/30 hover:text-[#2346ff]"
                aria-label="Fechar formulário"
              >
                <X className="h-4 w-4" weight="regular" />
              </button>
            </div>

            <form onSubmit={handleUserSubmit} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <div className="space-y-1.5">
                  <Label htmlFor="admin-user-name" className="text-xs font-bold text-slate-600">Nome completo</Label>
                  <Input
                    id="admin-user-name"
                    value={userForm.name}
                    onChange={event => updateUserForm("name", event.target.value)}
                    placeholder="Nome do colaborador"
                    className="h-10 rounded-xl border-slate-200 bg-slate-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="admin-user-email" className="text-xs font-bold text-slate-600">E-mail corporativo</Label>
                  <Input
                    id="admin-user-email"
                    type="email"
                    value={userForm.email}
                    onChange={event => updateUserForm("email", event.target.value)}
                    placeholder="nome@schumann.com.br"
                    className="h-10 rounded-xl border-slate-200 bg-slate-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="admin-user-cpf" className="text-xs font-bold text-slate-600">CPF</Label>
                  <Input
                    id="admin-user-cpf"
                    inputMode="numeric"
                    value={userForm.cpf}
                    onChange={event => updateUserForm("cpf", event.target.value)}
                    placeholder="000.000.000-00"
                    className="h-10 rounded-xl border-slate-200 bg-slate-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="admin-user-birth" className="text-xs font-bold text-slate-600">Data de nascimento</Label>
                  <Input
                    id="admin-user-birth"
                    type="date"
                    value={userForm.birth_date}
                    onChange={event => updateUserForm("birth_date", event.target.value)}
                    className="h-10 rounded-xl border-slate-200 bg-slate-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-600">Perfil</Label>
                  <Select value={userForm.role} onValueChange={value => updateUserForm("role", value)}>
                    <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-slate-50">
                      <SelectValue placeholder="Selecione o perfil" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="colaborador">Colaborador</SelectItem>
                      <SelectItem value="gerente">Gerente</SelectItem>
                      <SelectItem value="autor_area">Autor da área</SelectItem>
                      <SelectItem value="administrador_total">Administrador total</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-600">Status</Label>
                  <Select value={userForm.status} onValueChange={value => updateUserForm("status", value)}>
                    <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-slate-50">
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="inactive">Inativo</SelectItem>
                      <SelectItem value="blocked">Bloqueado</SelectItem>
                      <SelectItem value="away">Afastado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold text-slate-600">Setor</Label>
                  <Select value={userForm.department || "__none"} onValueChange={value => updateUserForm("department", value === "__none" ? "" : value)}>
                    <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-slate-50">
                      <SelectValue placeholder="Selecione o setor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Sem setor</SelectItem>
                      {activeDepartments.map(department => (
                        <SelectItem key={department.department_id || department.name} value={department.name}>
                          {department.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="admin-user-job" className="text-xs font-bold text-slate-600">Cargo</Label>
                  <Input
                    id="admin-user-job"
                    value={userForm.job_title}
                    onChange={event => updateUserForm("job_title", event.target.value)}
                    placeholder="Ex.: Analista"
                    className="h-10 rounded-xl border-slate-200 bg-slate-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="admin-user-registration" className="text-xs font-bold text-slate-600">Matrícula</Label>
                  <Input
                    id="admin-user-registration"
                    value={userForm.registration}
                    onChange={event => updateUserForm("registration", event.target.value)}
                    placeholder="Opcional"
                    className="h-10 rounded-xl border-slate-200 bg-slate-50"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="admin-user-phone" className="text-xs font-bold text-slate-600">Telefone</Label>
                  <Input
                    id="admin-user-phone"
                    value={userForm.phone}
                    onChange={event => updateUserForm("phone", event.target.value)}
                    placeholder="Opcional"
                    className="h-10 rounded-xl border-slate-200 bg-slate-50"
                  />
                </div>
              </div>

              <div className="rounded-2xl bg-[#fff8d7] px-4 py-3 text-xs leading-5 text-slate-700">
                Ao cadastrar sem senha, o colaborador deverá usar CPF + data de nascimento no primeiro acesso.
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeUserForm}
                  className="h-11 rounded-xl border-slate-200"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={savingUser}
                  className="h-11 rounded-xl bg-[#2346ff] text-white hover:bg-[#1d3ace]"
                >
                  {savingUser ? "Salvando..." : "Salvar usuário"}
                </Button>
              </div>
            </form>
          </section>
        )}

        <section className="bento-card p-5 lg:col-span-8">
          <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h2 className="font-heading text-base font-bold text-slate-900">Base de colaboradores</h2>
              <p className="text-xs text-slate-500">
                {filteredUsers.length} registro(s) exibido(s) de {users.length}.
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-[1fr_160px_150px]">
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                <Input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Buscar nome, CPF, setor..."
                  className="h-10 rounded-xl border-slate-200 bg-slate-50 pl-9"
                />
              </div>

              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-slate-50">
                  <SelectValue placeholder="Perfil" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos perfis</SelectItem>
                  <SelectItem value="colaborador">Colaborador</SelectItem>
                  <SelectItem value="gerente">Gerente</SelectItem>
                  <SelectItem value="autor_area">Autor da área</SelectItem>
                  <SelectItem value="administrador_total">Administrador total</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-slate-50">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos status</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="inactive">Inativo</SelectItem>
                  <SelectItem value="blocked">Bloqueado</SelectItem>
                  <SelectItem value="away">Afastado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-left">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="pb-3 font-bold">Usuário</th>
                  <th className="pb-3 font-bold">CPF</th>
                  <th className="pb-3 font-bold">Perfil</th>
                  <th className="pb-3 font-bold">Setor</th>
                  <th className="pb-3 font-bold">Status</th>
                  <th className="pb-3 font-bold">Acesso</th>
                  <th className="pb-3 font-bold">Atualizado</th>
                  <th className="pb-3 font-bold">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.user_id || user.email} className="border-b border-slate-100 last:border-0">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <Initials name={user.name} />
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                        <IdentificationCard className="h-4 w-4 text-[#2346ff]" weight="regular" />
                        {maskCpf(user.cpf)}
                      </div>
                    </td>
                    <td className="py-3">
                      <Badge className="bg-[#eef1ff] text-[#2346ff]">
                        {roleLabels[user.role] || user.role || "Não definido"}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <p className="text-sm font-medium text-slate-700">{user.department || "Sem setor"}</p>
                      <p className="text-xs text-slate-400">{user.job_title || "Cargo não informado"}</p>
                    </td>
                    <td className="py-3"><StatusBadge status={user.status} /></td>
                    <td className="py-3"><AccessBadge user={user} /></td>
                    <td className="py-3 text-sm text-slate-500">{formatDate(user.updated_at || user.sheet_last_updated_at)}</td>
                    <td className="py-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openEditUserForm(user)}
                        className="h-9 rounded-xl border-slate-200 text-slate-600 hover:border-[#2346ff]/30 hover:text-[#2346ff]"
                      >
                        <PencilSimple className="mr-1.5 h-4 w-4" weight="regular" />
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              Nenhum colaborador encontrado com os filtros atuais.
            </div>
          )}
        </section>

        <section className="bento-card p-5 lg:col-span-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-slate-900">Departamentos</h2>
            <Buildings className="h-5 w-5 text-[#2346ff]" weight="regular" />
          </div>

          <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_auto]">
            <Input
              value={newDepartment}
              onChange={event => setNewDepartment(event.target.value)}
              placeholder="Novo departamento"
              className="h-10 rounded-xl border-slate-200 bg-slate-50"
            />
            <Button
              type="button"
              onClick={createDepartment}
              disabled={departmentSaving || !newDepartment.trim()}
              className="h-10 rounded-xl bg-[#2346ff] text-white hover:bg-[#1d3ace]"
            >
              Criar
            </Button>
          </div>

          <div className="mb-4 space-y-2">
            {activeDepartments.map(department => (
              <div key={department.department_id || department.name} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <Buildings className="h-5 w-5 flex-shrink-0 text-[#2346ff]" weight="regular" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-slate-800">{department.name}</p>
                  <p className="text-xs text-slate-500">Disponivel para usuarios e conteudos</p>
                </div>
                <button
                  type="button"
                  onClick={() => deactivateDepartment(department)}
                  className="rounded-lg px-2 py-1 text-xs font-semibold text-rose-600 transition hover:bg-rose-50"
                >
                  Desativar
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Mais usados na base</p>
            {departments.map(([department, total]) => (
              <div key={department} className="flex items-center justify-between rounded-xl bg-white px-3 py-2 text-sm">
                <span className="truncate font-semibold text-slate-700">{department}</span>
                <span className="text-xs font-semibold text-slate-400">{total}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bento-card p-5 lg:col-span-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-slate-900">Regras aplicadas</h2>
            <FunnelSimple className="h-5 w-5 text-[#2346ff]" weight="regular" />
          </div>

          <div className="space-y-2">
            {[
              ["Colaborador", "Vê somente conteúdo marcado para colaborador."],
              ["Gerente", "Vê documentos e formulários marcados para gerentes por qualquer autor."],
              ["Autor da área", "Publica e organiza conteúdos do próprio setor."],
              ["Administrador total", "Gerencia usuários, setores, permissões, logs e integrações."],
            ].map(([title, helper]) => (
              <div key={title} className="flex gap-3 rounded-2xl bg-slate-50 p-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#2346ff]" weight="regular" />
                <div>
                  <p className="text-sm font-bold text-slate-800">{title}</p>
                  <p className="mt-0.5 text-xs leading-5 text-slate-500">{helper}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bento-card p-5 lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-slate-900">Checklist VPS</h2>
            <ListMagnifyingGlass className="h-5 w-5 text-[#2346ff]" weight="regular" />
          </div>

          <div className="space-y-2">
            {[
              ["Banco de dados", "MongoDB/PostgreSQL no VPS", Database, "text-[#2346ff]"],
              ["Backups", "Servidor + Google Drive", CheckCircle, "text-emerald-600"],
              ["Integração", "Planilha Google Sheets", PlugsConnected, "text-[#2346ff]"],
              ["Arquivos", "Servidor ou Drive por tipo", FileText, "text-[#2346ff]"],
              ["Desligados", "Bloqueio automático no sync", UserMinus, "text-amber-600"],
              ["Auditoria", "Logs por ação sensível", WarningCircle, "text-amber-600"],
            ].map(([title, helper, Icon, color]) => (
              <div key={title} className="flex gap-3 rounded-2xl bg-slate-50 p-3">
                <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${color}`} weight="regular" />
                <div>
                  <p className="text-sm font-bold text-slate-800">{title}</p>
                  <p className="mt-0.5 text-xs leading-5 text-slate-500">{helper}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
